import React, { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { Building2, ChevronDown, Check, QrCode } from 'lucide-react';
import { useAuth } from '../context/AuthContext';

const API_BASE = process.env.REACT_APP_BACKEND_URL;

/**
 * ClubSwitcher - dropdown pill shown in the header that lists all clubs
 * the current user is a member of. Lets them switch active club without logging out.
 *
 * Only renders if multi_club_enabled feature flag is ON and the user has >= 2 memberships.
 */
const ClubSwitcher = ({ compact = false, logo = false }) => {
  const { user, switchOrganization } = useAuth();
  const [memberships, setMemberships] = useState([]);
  const [open, setOpen] = useState(false);
  const [switching, setSwitching] = useState(null);
  const [loaded, setLoaded] = useState(false);
  const containerRef = useRef(null);

  // Close dropdown on outside click
  useEffect(() => {
    const handleClick = (e) => {
      if (containerRef.current && !containerRef.current.contains(e.target)) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, []);

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      try {
        // Check feature flag first
        const flagsRes = await axios.get(`${API_BASE}/api/feature-flags`);
        if (cancelled) return;
        if (!flagsRes.data?.multi_club_enabled) {
          setLoaded(true);
          return;
        }
        const token = localStorage.getItem('token');
        if (!token) {
          setLoaded(true);
          return;
        }
        const res = await axios.get(`${API_BASE}/api/auth/memberships`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (!cancelled) {
          setMemberships(res.data?.memberships || []);
          setLoaded(true);
        }
      } catch (e) {
        if (!cancelled) setLoaded(true);
      }
    };
    if (user?.email && user?.role !== 'super_admin') load();
    else setLoaded(true);
    return () => { cancelled = true; };
  }, [user?.email, user?.role, user?.organization_id]);

  // Don't show pill for super admins or single-club users.
  // Special case: `logo` variant renders the static logo as a fallback for single-club users.
  if (!loaded || memberships.length < 2 || user?.role === 'super_admin') {
    if (logo) {
      return (
        <Link to="/" className="flex items-center space-x-3">
          <div className="h-8 w-8 bg-yellow-500 rounded flex items-center justify-center">
            <QrCode className="h-5 w-5 text-gray-900" />
          </div>
          <div className="block">
            <div className="text-white font-bold text-lg">
              {user?.organization?.name || 'Aktivlogg'}
            </div>
            <div className="text-yellow-500 text-xs hidden sm:block">
              Digital Treningslogg
            </div>
          </div>
        </Link>
      );
    }
    return null;
  }

  const currentMembership = memberships.find((m) => m.is_current) || memberships[0];

  const handleSwitch = async (orgId) => {
    if (orgId === user?.organization_id) {
      setOpen(false);
      return;
    }
    setSwitching(orgId);
    const result = await switchOrganization(orgId);
    setSwitching(null);
    setOpen(false);
    if (result.success) {
      // Reload entire app to ensure all data is re-fetched in new club context
      window.location.reload();
    }
  };

  return (
    <div className="relative" ref={containerRef}>
      <button
        type="button"
        onClick={() => setOpen(!open)}
        className={
          logo
            ? "flex items-center space-x-3 group focus:outline-none"
            : compact
              ? "flex items-center justify-center h-9 w-9 bg-gray-800 hover:bg-gray-700 border border-yellow-500/40 hover:border-yellow-500 rounded-full text-white transition-all relative"
              : "flex items-center gap-2 px-3 py-1.5 bg-gray-800 hover:bg-gray-700 border border-yellow-500/40 hover:border-yellow-500 rounded-full text-xs font-medium text-white transition-all"
        }
        data-testid="club-switcher-button"
        title={compact ? `Aktiv klubb: ${currentMembership?.organization_name}. Trykk for å bytte.` : 'Bytt klubb'}
        aria-label={`Bytt klubb (aktiv: ${currentMembership?.organization_name})`}
      >
        {logo ? (
          <>
            <div className="h-8 w-8 bg-yellow-500 rounded flex items-center justify-center flex-shrink-0">
              <QrCode className="h-5 w-5 text-gray-900" />
            </div>
            <div className="text-left">
              <div className="text-white font-bold text-lg flex items-center gap-1.5">
                <span className="truncate max-w-[180px]">{currentMembership?.organization_name}</span>
                <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full bg-yellow-500/15 border border-yellow-500/40 text-[10px] text-yellow-400 font-bold">
                  {memberships.length}
                  <ChevronDown className={`h-3 w-3 transition-transform ${open ? 'rotate-180' : ''}`} />
                </span>
              </div>
              <div className="text-yellow-500 text-xs hidden sm:block">Bytt klubb</div>
            </div>
          </>
        ) : (
          <>
            <Building2 className={compact ? 'h-4 w-4 text-yellow-500' : 'h-3.5 w-3.5 text-yellow-500'} />
            {!compact && (
              <>
                <span className="max-w-[140px] truncate">{currentMembership?.organization_name}</span>
                <ChevronDown className={`h-3.5 w-3.5 transition-transform ${open ? 'rotate-180' : ''}`} />
              </>
            )}
            {compact && (
              <span className="absolute -top-1 -right-1 h-4 w-4 bg-yellow-500 text-gray-900 text-[9px] font-bold rounded-full flex items-center justify-center">
                {memberships.length}
              </span>
            )}
          </>
        )}
      </button>

      {open && (
        <div
          className={`absolute ${logo ? 'left-0' : 'right-0'} mt-2 w-72 bg-gray-800 border border-gray-700 rounded-xl shadow-2xl py-2 z-50`}
          data-testid="club-switcher-dropdown"
        >
          <div className="px-3 py-2 text-[10px] uppercase tracking-widest text-gray-400 font-bold border-b border-gray-700">
            Dine klubber
          </div>
          {memberships.map((m) => {
            const isActive = m.organization_id === user?.organization_id;
            const isSwitching = switching === m.organization_id;
            return (
              <button
                key={m.organization_id}
                onClick={() => handleSwitch(m.organization_id)}
                disabled={!!switching}
                className={`w-full flex items-center gap-3 px-3 py-2.5 hover:bg-gray-700 transition-colors text-left ${
                  isActive ? 'bg-gray-700/50' : ''
                }`}
                data-testid={`club-switcher-option-${m.organization_id}`}
              >
                <div className="flex-shrink-0 h-9 w-9 rounded-lg bg-gray-700 flex items-center justify-center overflow-hidden">
                  {m.organization_logo ? (
                    <img src={m.organization_logo} alt="" className="h-full w-full object-cover" />
                  ) : (
                    <Building2 className="h-4 w-4 text-yellow-500" />
                  )}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="text-sm text-white font-medium truncate">
                    {m.organization_name}
                  </div>
                  <div className="text-xs text-gray-400 truncate">
                    {m.member_number || m.role}
                  </div>
                </div>
                {isSwitching ? (
                  <div className="h-4 w-4 border-2 border-yellow-500 border-t-transparent rounded-full animate-spin"></div>
                ) : isActive ? (
                  <Check className="h-4 w-4 text-yellow-500" />
                ) : null}
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default ClubSwitcher;
